import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import {
    Database,
    RefreshCw,
    Play,
    Loader2,
    CheckCircle,
    AlertCircle,
    Users,
    Zap,
    TrendingUp,
    Trash2,
    CheckSquare
} from 'lucide-react';

const CandidatePoolManager = () => {
    const { api } = useAuth();
    const [loading, setLoading] = useState(true);
    const [poolStatus, setPoolStatus] = useState(null);
    const [poolStats, setPoolStats] = useState(null);
    const [generatingTask, setGeneratingTask] = useState(null);
    const [selectedLab, setSelectedLab] = useState(null);
    const [selectedDifficulty, setSelectedDifficulty] = useState(null);
    const [generateCount, setGenerateCount] = useState(5);
    
    // Multi-select state for bulk delete
    const [selectedPools, setSelectedPools] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectMode, setSelectMode] = useState(false);

    const fetchPoolStatus = useCallback(async () => {
        try {
            const [statusRes, statsRes] = await Promise.all([
                api.get('/sourcing-labs/admin/pool/status'),
                api.get('/sourcing-labs/admin/pool/stats').catch(() => ({ data: null }))
            ]);
            setPoolStatus(statusRes.data);
            setPoolStats(statsRes.data);
        } catch (err) {
            console.error('Failed to fetch pool status:', err);
            toast.error('Failed to load pool status');
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchPoolStatus();
    }, [fetchPoolStatus]);

    // Poll for generation task status
    useEffect(() => {
        if (!generatingTask?.task_id) return;

        const interval = setInterval(async () => {
            try {
                const res = await api.get(`/sourcing-labs/admin/pool/generate/${generatingTask.task_id}`);
                const taskStatus = res.data;

                if (taskStatus.status === 'completed') {
                    setGeneratingTask(null);
                    toast.success('Pool generation completed!');
                    fetchPoolStatus();
                    clearInterval(interval);
                } else if (taskStatus.status === 'failed') {
                    setGeneratingTask(null);
                    toast.error(`Generation failed: ${taskStatus.error}`);
                    clearInterval(interval);
                } else {
                    setGeneratingTask(prev => ({
                        ...prev,
                        progress: taskStatus.progress,
                        total: taskStatus.total
                    }));
                }
            } catch (err) {
                // Ignore polling errors
            }
        }, 3000);

        return () => clearInterval(interval);
    }, [generatingTask?.task_id, api, fetchPoolStatus]);

    const startGeneration = async (labId, difficulty) => {
        try {
            const res = await api.post('/sourcing-labs/admin/pool/generate', {
                lab_id: labId,
                difficulty: difficulty,
                count: generateCount
            });

            if (res.data.status === 'started') {
                setGeneratingTask({
                    task_id: res.data.task_id,
                    lab_id: labId,
                    difficulty: difficulty,
                    progress: 0,
                    total: res.data.generating_count
                });
                toast.success(`Generating ${res.data.generating_count} candidates!`);
            } else if (res.data.status === 'already_running') {
                toast.info('Generation already in progress');
            } else if (res.data.status === 'pool_full') {
                toast.info(res.data.message);
            }
        } catch (err) {
            toast.error('Failed to start generation');
        }
    };

    const togglePoolSelection = (poolKey) => {
        setSelectedPools(prev => {
            if (prev.includes(poolKey)) {
                return prev.filter(k => k !== poolKey);
            } else {
                return [...prev, poolKey];
            }
        });
    };

    const selectAllPools = () => {
        if (!poolStatus?.pools) return;
        const allKeys = poolStatus.pools.map(p => `${p.lab_id}_${p.difficulty}`);
        setSelectedPools(allKeys);
    };

    const clearSelection = () => {
        setSelectedPools([]);
    };

    const handleBulkDelete = async () => {
        if (selectedPools.length === 0) {
            toast.error('No pools selected');
            return;
        }

        if (!window.confirm(`Delete ${selectedPools.length} pool(s)? This will remove all candidates in these pools.`)) {
            return;
        }

        setIsDeleting(true);
        try {
            const res = await api.post('/sourcing-labs/admin/pool/bulk-delete', {
                pool_keys: selectedPools
            });

            if (res.data.success) {
                toast.success(`Deleted ${res.data.total_deleted} candidates from ${res.data.pools_deleted} pool(s)`);
                setSelectedPools([]);
                setSelectMode(false);
                fetchPoolStatus();
            }
        } catch (err) {
            toast.error('Failed to delete pools');
        } finally {
            setIsDeleting(false);
        }
    };

    const startGenerateAll = async () => {
        if (!window.confirm('This will generate candidates for ALL pools. This may take a while. Continue?')) {
            return;
        }

        try {
            const res = await api.post('/sourcing-labs/admin/pool/generate-all');
            setGeneratingTask({
                task_id: res.data.task_id,
                type: 'all',
                progress: 0
            });
            toast.success('Started generating all pools!');
        } catch (err) {
            toast.error('Failed to start generation');
        }
    };

    if (loading) {
        return (
            <Card>
                <CardContent className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </CardContent>
            </Card>
        );
    }

    // Group pools by lab
    const poolsByLab = {};
    poolStatus?.pools?.forEach(pool => {
        if (!poolsByLab[pool.lab_id]) {
            poolsByLab[pool.lab_id] = {
                title: pool.lab_title,
                pools: []
            };
        }
        poolsByLab[pool.lab_id].pools.push(pool);
    });

    return (
        <Card data-testid="candidate-pool-manager">
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-100 text-blue-600">
                            <Database className="h-5 w-5" />
                        </div>
                        <div>
                            <CardTitle className="text-lg">Candidate Pool Manager</CardTitle>
                            <CardDescription>
                                Pre-generate candidates for instant session starts & LLM cost savings
                            </CardDescription>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {selectMode ? (
                            <>
                                <Badge variant="outline" className="bg-red-50">
                                    {selectedPools.length} selected
                                </Badge>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={selectAllPools}
                                >
                                    <CheckSquare className="h-4 w-4 mr-1" />
                                    Select All
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={clearSelection}
                                >
                                    Clear
                                </Button>
                                <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={handleBulkDelete}
                                    disabled={selectedPools.length === 0 || isDeleting}
                                >
                                    {isDeleting ? (
                                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                    ) : (
                                        <Trash2 className="h-4 w-4 mr-1" />
                                    )}
                                    Delete ({selectedPools.length})
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        setSelectMode(false);
                                        setSelectedPools([]);
                                    }}
                                >
                                    Cancel
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setSelectMode(true)}
                                    className="text-red-600 border-red-200 hover:bg-red-50"
                                >
                                    <Trash2 className="h-4 w-4 mr-1" />
                                    Delete Pools
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={fetchPoolStatus}
                                    disabled={loading}
                                >
                                    <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                                    Refresh
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={startGenerateAll}
                                    disabled={!!generatingTask}
                                    className="bg-blue-600 hover:bg-blue-700"
                                >
                                    <Zap className="h-4 w-4 mr-1" />
                                    Generate All
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Generation Progress */}
                {generatingTask && (
                    <Card className="border-blue-200 bg-blue-50">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                                <div className="flex-1">
                                    <p className="font-medium text-blue-900">
                                        Generating candidates...
                                    </p>
                                    <p className="text-sm text-blue-700">
                                        {generatingTask.type === 'all' 
                                            ? 'Processing all pools' 
                                            : `${generatingTask.lab_id} / ${generatingTask.difficulty}`
                                        }
                                    </p>
                                    {generatingTask.total > 0 && (
                                        <Progress 
                                            value={(generatingTask.progress / generatingTask.total) * 100} 
                                            className="mt-2 h-2"
                                        />
                                    )}
                                </div>
                                <Badge variant="outline" className="bg-white">
                                    {generatingTask.progress || 0} generated
                                </Badge>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold text-blue-600">
                                {poolStats?.total_candidates || 0}
                            </p>
                            <p className="text-xs text-muted-foreground">Total Candidates</p>
                        </CardContent>
                    </Card>
                    <Card className={poolStatus?.summary?.pools_ready > 0 ? 'border-green-200 bg-green-50' : ''}>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold text-green-600">
                                {poolStatus?.summary?.pools_ready || 0}
                            </p>
                            <p className="text-xs text-muted-foreground">Pools Ready (60+)</p>
                        </CardContent>
                    </Card>
                    <Card className={poolStatus?.summary?.pools_empty > 0 ? 'border-amber-200 bg-amber-50' : ''}>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold text-amber-600">
                                {poolStatus?.summary?.pools_empty || 0}
                            </p>
                            <p className="text-xs text-muted-foreground">Pools Empty</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold">
                                {poolStats?.usage_rate || 0}%
                            </p>
                            <p className="text-xs text-muted-foreground">Usage Rate</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Quick Generate */}
                <Card>
                    <CardContent className="p-4">
                        <div className="flex flex-wrap items-center gap-3">
                            <span className="text-sm font-medium">Quick Generate:</span>
                            <Select 
                                value={selectedLab || ''} 
                                onValueChange={setSelectedLab}
                            >
                                <SelectTrigger className="w-[200px]">
                                    <SelectValue placeholder="Select Lab" />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(poolsByLab).map(([labId, lab]) => (
                                        <SelectItem key={labId} value={labId}>
                                            {lab.title}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select 
                                value={selectedDifficulty || ''} 
                                onValueChange={setSelectedDifficulty}
                            >
                                <SelectTrigger className="w-[150px]">
                                    <SelectValue placeholder="Difficulty" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="beginner">Beginner</SelectItem>
                                    <SelectItem value="intermediate">Intermediate</SelectItem>
                                    <SelectItem value="advanced">Advanced</SelectItem>
                                    <SelectItem value="expert">Expert</SelectItem>
                                </SelectContent>
                            </Select>
                            <div className="flex items-center gap-1">
                                <Input
                                    type="number"
                                    value={generateCount}
                                    onChange={(e) => setGenerateCount(Math.min(5, Math.max(1, parseInt(e.target.value) || 1)))}
                                    className="w-16 text-center"
                                    min={1}
                                    max={5}
                                />
                                <span className="text-xs text-muted-foreground">candidates</span>
                            </div>
                            <Button
                                size="sm"
                                onClick={() => startGeneration(selectedLab, selectedDifficulty)}
                                disabled={!selectedLab || !selectedDifficulty || !!generatingTask}
                            >
                                <Play className="h-4 w-4 mr-1" />
                                Generate
                            </Button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                            Max 5 candidates per request. Pool max size: 100 candidates.
                        </p>
                    </CardContent>
                </Card>

                {/* Pools by Lab */}
                <div className="space-y-4">
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Pool Status by Lab
                    </h3>
                    
                    {Object.entries(poolsByLab).map(([labId, lab]) => (
                        <Card key={labId}>
                            <CardContent className="p-4">
                                <h4 className="font-medium mb-3">{lab.title}</h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    {lab.pools.map(pool => {
                                        const poolKey = `${pool.lab_id}_${pool.difficulty}`;
                                        const isSelected = selectedPools.includes(poolKey);
                                        
                                        return (
                                            <div 
                                                key={poolKey}
                                                className={`p-3 rounded-lg border relative ${
                                                    selectMode && isSelected
                                                        ? 'ring-2 ring-red-500 bg-red-50 border-red-300'
                                                        : pool.ready 
                                                            ? 'bg-green-50 border-green-200' 
                                                            : pool.count > 0 
                                                                ? 'bg-amber-50 border-amber-200'
                                                                : 'bg-slate-50 border-slate-200'
                                                }`}
                                                onClick={selectMode ? () => togglePoolSelection(poolKey) : undefined}
                                                style={selectMode ? { cursor: 'pointer' } : {}}
                                            >
                                                {selectMode && (
                                                    <div className="absolute top-2 left-2">
                                                        <Checkbox
                                                            checked={isSelected}
                                                            onCheckedChange={() => togglePoolSelection(poolKey)}
                                                        />
                                                    </div>
                                                )}
                                                <div className={`flex items-center justify-between mb-1 ${selectMode ? 'ml-6' : ''}`}>
                                                    <span className="text-sm font-medium capitalize">
                                                        {pool.difficulty}
                                                    </span>
                                                    {pool.ready ? (
                                                        <CheckCircle className="h-4 w-4 text-green-600" />
                                                    ) : pool.count > 0 ? (
                                                        <TrendingUp className="h-4 w-4 text-amber-600" />
                                                    ) : (
                                                        <AlertCircle className="h-4 w-4 text-slate-400" />
                                                    )}
                                                </div>
                                                <p className="text-lg font-bold">
                                                    {pool.count}/{pool.target}
                                                </p>
                                                <Progress 
                                                    value={pool.percentage} 
                                                    className="h-1.5 mt-1"
                                                />
                                                {!selectMode && !pool.ready && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="mt-2 w-full h-7 text-xs"
                                                        onClick={() => startGeneration(pool.lab_id, pool.difficulty)}
                                                        disabled={!!generatingTask}
                                                    >
                                                        Generate
                                                    </Button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* Archetype Distribution */}
                {poolStats?.by_archetype && Object.keys(poolStats.by_archetype).length > 0 && (
                    <div>
                        <h3 className="text-sm font-semibold mb-3">Archetype Distribution</h3>
                        <div className="flex flex-wrap gap-2">
                            {Object.entries(poolStats.by_archetype).map(([archetype, count]) => (
                                <Badge key={archetype} variant="outline" className="text-xs">
                                    {archetype.replace('_', ' ')}: {count}
                                </Badge>
                            ))}
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default CandidatePoolManager;
